import type { Viewer } from '@crm/contracts';
import { useQuery } from '@tanstack/react-query';
import type { ReactElement } from 'react';

import { platformOrganizationsQuery } from '@/entities/platform';
import { organizationAccess } from '@/entities/session';
import { paths } from '@/shared/config';

import { NavigationGroup } from './NavigationGroup';
import { OrganizationNavigation } from './OrganizationNavigation';
import { type OrganizationOption, OrganizationSwitcher } from './OrganizationSwitcher';
import styles from './Sidebar.module.scss';

interface SidebarProps {
  viewer: Viewer;
  organizationId: number | null;
  onNavigate: () => void;
}

function useOrganizations(viewer: Viewer): ReadonlyArray<OrganizationOption> {
  const all = useQuery({ ...platformOrganizationsQuery, enabled: viewer.isGlobalAdmin });
  return viewer.isGlobalAdmin ? (all.data ?? []) : viewer.organizations;
}

function AdminNavigation({ onNavigate }: Pick<SidebarProps, 'onNavigate'>): ReactElement {
  return (
    <NavigationGroup
      title="Администрирование"
      items={[
        { to: paths.adminOrganizations, label: 'Все организации', icon: 'building' },
        { to: paths.adminUsers, label: 'Пользователи', icon: 'users' },
      ]}
      onNavigate={onNavigate}
    />
  );
}

function SidebarGroups({ viewer, organizationId, onNavigate }: SidebarProps): ReactElement {
  const { canManage, can } = organizationAccess(viewer, organizationId ?? 0);
  return (
    <div className={styles.groups}>
      {viewer.isGlobalAdmin && <AdminNavigation onNavigate={onNavigate} />}
      {organizationId !== null && (
        <OrganizationNavigation
          organizationId={organizationId}
          canManage={canManage}
          can={can}
          onNavigate={onNavigate}
        />
      )}
    </div>
  );
}

export function Sidebar(props: SidebarProps): ReactElement {
  const organizations = useOrganizations(props.viewer);
  return (
    <nav className={styles.nav} aria-label="Разделы">
      {organizations.length > 0 && (
        <OrganizationSwitcher organizations={organizations} {...props} />
      )}
      <SidebarGroups {...props} />
    </nav>
  );
}
